import {
  getAccessTokenRefreshDelay,
  performRefresh,
} from 'modules/user/silentRefresh';
import { getLocalAccessToken } from 'modules/user/userCookies';
import { sleep } from 'utils/sleep';

interface Props {
  sendAuth: () => void;
}

export const handleAccessTokenRefresh = async ({ sendAuth }: Props) => {
  const token = getLocalAccessToken();

  if (!token) {
    console.warn('No local token found, skipping refresh.');
    return;
  }

  const auth = () =>
    setTimeout(() => {
      sendAuth();
    }, 100);

  if (token) {
    const refreshTime = getAccessTokenRefreshDelay(token, 5);

    if (refreshTime <= 0) {
      try {
        await performRefresh({
          isNotSilentRefresh: true,
          onTokenRefreshed: auth,
        });
      } catch (err) {
        console.error('Silent refresh failed at startup', err);
      }
    } else {
      auth();
    }
  }
};

export const waitUntilTokenIsFresh = async () => {
  let retries = 0;
  const maxRetries = 50; // ~5 seconds
  const token = getLocalAccessToken();

  const refreshTime = token ? getAccessTokenRefreshDelay(token, 5) : null;
  await sleep(300);
  while (refreshTime && refreshTime <= 0) {
    await sleep(100);
    retries++;
    if (retries >= maxRetries) throw new Error('Token refresh timeout');
  }
};

export const withTimeout = (promise: Promise<any>, ms: number) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout exceeded')), ms)
    ),
  ]);
};

import { createContext, useState, useContext, ReactNode } from 'react';

interface ConnectionErrorContextType {
  criticalError: boolean;
  setCriticalError: (value: boolean) => void;
}

const ConnectionErrorContext = createContext<
  ConnectionErrorContextType | undefined
>(undefined);

export const ConnectionErrorProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [criticalError, setCriticalError] = useState(false);

  return (
    <ConnectionErrorContext.Provider
      value={{ criticalError, setCriticalError }}
    >
      {children}
    </ConnectionErrorContext.Provider>
  );
};

export const useConnectionError = () => {
  const context = useContext(ConnectionErrorContext);
  if (!context) {
    throw new Error(
      'useConnectionError must be used inside a ConnectionErrorProvider'
    );
  }
  return context;
};
